import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Breadcrumbs,
  Link,
  LinearProgress,
} from "@mui/material";
import TagIcon from "@mui/icons-material/Tag";
import ExtensionIcon from "@mui/icons-material/Extension";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import PublicIcon from "@mui/icons-material/Public";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import BuildIcon from "@mui/icons-material/Build";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { CheckHexagon, Activity, Tool } from "@mynaui/icons-react";
import Footer from "../../../shared/components/Footer";
import ConnectedSensors from "../components/ConnectedSensors";
import LogsAndEvents from "../components/LogsAndEvents";
import RiskAlertHistory from "../components/RiskAlertHistory";
import ServiceHistory from "../components/ServiceHistory";
import WorkOrdersSection from "../components/WorkOrdersSection";
import CommentsSection from "../components/CommentsSection";
import HealthCheckModal from "../../dashboard/components/HealthCheckModal";
import PartsAvailabilityModal from "../../dashboard/components/PartsAvailabilityModal";
import ServiceProgressModal from "../../dashboard/components/ServiceProgressModal";
import { useAuth } from "../../../shared/context/AuthContext";
import { ROLES } from "../../../shared/config/roleConfig";
import { useZone } from "../../../shared/context/ZoneContext";
import { getAssetsByZone } from "../data/assetsMockData";
import { getAssetDetailExtras } from "../data/assetDetailMockData";

const detailFields = [
  { key: "id", label: "ID", icon: TagIcon },
  { key: "model", label: "Model", icon: ExtensionIcon },
  { key: "statusField", label: "Status", icon: WarningAmberIcon },
  { key: "type", label: "Type", icon: FilterAltIcon },
  { key: "make", label: "Make", icon: PrecisionManufacturingIcon },
  { key: "detailLocation", label: "Location", icon: PublicIcon },
];

const getStatusColor = (healthScore) => {
  if (healthScore < 60) return "#DC2626";
  if (healthScore < 80) return "#D97706";
  return "#2E7628";
};

const getStatusLabel = (healthScore) => {
  if (healthScore < 60) return "Critical";
  if (healthScore < 80) return "Warning";
  return "Good";
};

const AssetDetailPage = () => {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedZone } = useZone();
  const isTechnician = user?.role === ROLES.TECHNICIAN;

  // Find asset within the current zone's assets
  const zoneAssets = getAssetsByZone(selectedZone);
  const asset = zoneAssets.find((a) => String(a.id) === String(assetId)) || null;
  const extras = getAssetDetailExtras(assetId, selectedZone);

  const [healthCheckOpen, setHealthCheckOpen] = useState(false);
  const [partsCheckOpen, setPartsCheckOpen] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [partsAvailability, setPartsAvailability] = useState(null);
  const [serviceProgressOpen, setServiceProgressOpen] = useState(false);
  const [serviceComplete, setServiceComplete] = useState(false);

  if (!asset) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h5">Asset not found</Typography>
        <Button onClick={() => navigate("/assets")} sx={{ mt: 2 }}>
          Back to Assets
        </Button>
      </Box>
    );
  }

  const getHealthScoreColor = (score) => {
    if (score >= 70) return "#399432";
    if (score >= 60) return "#FFC107";
    return "#F44336";
  };
  
  const handleStartService = () => {
    setServiceProgressOpen(true);
    setServiceComplete(false);
  };

  const handleServiceComplete = () => {
    setServiceComplete(true);
  };

  const handleServiceModalClose = () => {
    setServiceProgressOpen(false);
    // if (serviceComplete) {
    //   setHealthCheckOpen(true);
    // }
  };

  const handleHealthCheckComplete = (data) => {
    setHealthData(data);
    setPartsAvailability(data.partsAvailable);
  };

  const handlePartsCheck = () => {
    setPartsCheckOpen(true);
  };

  const handlePartsCheckComplete = (partsAvailable) => {
    setPartsAvailability(partsAvailable);
    setPartsCheckOpen(false);
  };

  const statusColor = getStatusColor(asset.healthScore);
  const statusLabel = getStatusLabel(asset.healthScore);
  const isEmergency =
    asset.recommendedAction.severity === "Emergency Maintenance";

  return (
    <Box sx={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3, flexGrow: 1 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, fontSize: "1.375rem" }}
          >
            {asset.fullName || asset.name}
          </Typography>
          {isTechnician && (
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              {partsAvailability !== null && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                  }}
                  onClick={handlePartsCheck}
                >
                  <CheckHexagon
                    style={{
                      fontSize: 20,
                      color: partsAvailability ? "#2E7628" : "#DC2626",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: partsAvailability ? "#2E7628" : "#DC2626",
                      fontWeight: 500,
                      fontSize: "0.875rem",
                    }}
                  >
                    {partsAvailability
                      ? "All Parts Available"
                      : "Parts Not Available"}
                  </Typography>
                </Box>
              )}
              <Button
                variant="outlined"
                startIcon={<Activity />}
                onClick={() => setHealthCheckOpen(true)}
                sx={{
                  borderColor: "#E5E7EB",
                  color: "text.primary",
                  fontWeight: 600,
                  px: 2.5,
                  textTransform: "none",
                  "&:hover": { borderColor: "#D1D5DB", bgcolor: "#F9FAFB" },
                }}
              >
                Run Health Check
              </Button>
              <Button
                variant="contained"
                startIcon={<Tool />}
                onClick={handleStartService}
                //disabled={partsAvailability !== true}
                sx={{
                  bgcolor: "#399432",
                  color: "#fff",
                  fontWeight: 600,
                  px: 2.5,
                  textTransform: "none",
                  "&:hover": { bgcolor: "#2E7628" },
                }}
              >
                Start Service
              </Button>
            </Box>
          )}
        </Box>

        {/* Breadcrumb */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 3 }}
        >
          <Link
            component="button"
            underline="hover"
            onClick={() => {
              !isTechnician ? navigate("/assets") : navigate("/dashboard");
            }}
            sx={{ color: "text.secondary", fontSize: "0.875rem" }}
          >
            Assets
          </Link>
          <Typography
            sx={{
              fontSize: "0.875rem",
              color: "text.primary",
              fontWeight: 500,
            }}
          >
            {asset.fullName || asset.name}
          </Typography>
        </Breadcrumbs>

        {/* Asset Info Section */}
        <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
          {/* Image */}
          {/* Image */}
          <Card
            sx={{
              flex: "0 0 45%",
              overflow: "hidden",
              borderRadius: 0.5,
              height: 380,
            }}
          >
            <Box
              component="img"
              src={asset.image}
              alt={asset.fullName || asset.name}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </Card>

          {/* Detail Fields */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: 380,
            }}
          >
            {detailFields.map((field) => {
              const Icon = field.icon;
              let value = asset[field.key];
              let valueColor = "text.primary";

              if (field.key === "statusField") {
                value = statusLabel;
                valueColor = statusColor;
              }

              return (
                <Card
                  key={field.key}
                  sx={{ borderRadius: 1, border: "1px solid #E5E7EB" }}
                >
                  <CardContent
                    sx={{
                      p: 1.25,
                      px: 1.5,
                      "&:last-child": { pb: .5 },
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <Icon sx={{ fontSize: 20, color: "text.secondary" }} />
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        {field.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.9375rem",
                          color: valueColor,
                        }}
                      >
                        {value}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>

        {/* Bottom Cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: 2.5,
            mb: 3,
          }}
        >
          {/* Health Score */}
          <Card sx={{ flex: 1, borderRadius: 1 }}>
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.9375rem",
                    color: "text.primary",
                  }}
                >
                  Health Score
                </Typography>
                <ShowChartIcon sx={{ fontSize: 22, color: "text.secondary" }} />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "2rem",
                  color: "text.primary",
                  mb: 1,
                }}
              >
                {asset.healthScore}/100
              </Typography>
              <LinearProgress
                variant="determinate"
                value={asset.healthScore}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "#E5E7EB"
                  ,
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: getHealthScoreColor(asset.healthScore),
                    borderRadius: 1,
                  },
                }}
              />
            </CardContent>
          </Card>

          {/* RUL */}
          <Card sx={{ flex: 1, borderRadius: 1, border: "1px solid #E0E0E0" }}>
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.9375rem",
                    color: "text.primary",
                  }}
                >
                  RUL
                </Typography>
                <CalendarTodayIcon
                  sx={{ fontSize: 22, color: "text.secondary" }}
                />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "2rem",
                  color: "text.primary",
                  mb: 0.5,
                }}
              >
                {asset.rul?.value || "N/A"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontSize: "0.8125rem" }}
              >
                Remaining Useful Life
              </Typography>
            </CardContent>
          </Card>

          {/* Failure Probability */}
          <Card sx={{ flex: 1, borderRadius: 1, border: "1px solid #E0F2FE" }}>
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.9375rem",
                    color: "text.primary",
                  }}
                >
                  Failure Probability
                </Typography>
                <WarningAmberIcon
                  sx={{ fontSize: 22, color: "text.secondary" }}
                />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "2rem",
                  color: "text.primary",
                }}
              >
                {asset.failureProbability}%
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontSize: "0.8125rem" }}
              >
                {asset.failureTimeframe}
              </Typography>
            </CardContent>
          </Card>

          {/* Recommended Action */}
          <Card
            sx={{
              flex: 1,
              borderRadius: 1,
              bgcolor: isEmergency ? "#FEF2F2" : "#FFF7ED",
              border: `1px solid ${isEmergency ? "#FECACA" : "#FED7AA"}`,
            }}
          >
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.9375rem",
                    color: "text.primary",
                  }}
                >
                  Recommended Action
                </Typography>
                <BuildIcon sx={{ fontSize: 22, color: "text.secondary" }} />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.9375rem",
                  color: isEmergency ? "#DC2626" : "#D97706",
                  mb: 0.5,
                }}
              >
                {asset.recommendedAction.severity}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: isEmergency ? "#DC2626" : "#92400E",
                  fontSize: "0.8125rem",
                }}
              >
                {asset.recommendedAction.description}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Connected Sensors - hidden for technician */}
        <ConnectedSensors sensors={extras.connectedSensors} />

        {/* Sections below sensor readings - hidden for technician */}
        {/* Logs and Events */}
        <LogsAndEvents data={extras.logsAndEvents} />
        <>

          {/* Risk Alert History + Service History (side by side) */}
          <Box sx={{ display: "flex", gap: 2.5, mb: 3 }}>
            <RiskAlertHistory alerts={extras.riskAlertHistory} />
            <ServiceHistory services={extras.serviceHistory} />
          </Box>

          {/* Work Orders */}
          <WorkOrdersSection workOrders={extras.workOrders} />

          {/* Comments */}
          <CommentsSection comments={extras.comments} />
        </>
      </Box>
      <Footer />

      {/* Technician Modals */}
      {isTechnician && (
        <>
          <HealthCheckModal
            open={healthCheckOpen}
            onClose={() => setHealthCheckOpen(false)}
            onHealthCheckComplete={handleHealthCheckComplete}
            assetId={assetId}
            assetName={asset.fullName || asset.name}
            zoneId={selectedZone}
          />
          <PartsAvailabilityModal
            open={partsCheckOpen}
            onClose={() => setPartsCheckOpen(false)}
            onComplete={handlePartsCheckComplete}
            healthData={healthData}
          />
          <ServiceProgressModal
            open={serviceProgressOpen}
            onClose={handleServiceModalClose}
            onComplete={handleServiceComplete}
            isComplete={serviceComplete}
          />
        </>
      )}
    </Box>
  );
};

export default AssetDetailPage;
